import { Modal } from 'semantic-ui-react';
import { formatMoneyWithCurrency } from 'src/utils/formatter';
import ModalActionsFooter from 'src/components/shared/ModalActionsFooter';
import { getCustomModalContentStyle } from './customModal.constants';
import './customModal.scss';

/**
 * Standard app modal: salmon header (see customModal.scss), scroll rules for known content classes,
 * footer via ModalActionsFooter or customActions.
 *
 * Dense form styling for Modal.Content is defined globally in `src/scss/_scfo-modal-form.scss`
 * (classes like `.scfo-modal-form-body`, `.create-company-modal-content`, `.create-obligation-modal-content`).
 *
 * Props:
 * - open, onClose, content (ReactNode), size, dimmer, scrolling, classNameContent, etc.
 * - Prefer default footer: confirmLabel + onConfirm + cancelarLabel/onClose (ScfoButton ghost + primary).
 * - customActions: solo si necesitas layout extra; los .ui.button en .actions heredan estilos de customModal.scss.
 * - hideActions: omit Modal.Actions entirely (e.g. read-only content).
 * - hideHeader: omit Modal.Header.
 * - renderHeader: if set, replaces default Modal.Header (use for custom title row + close icon).
 * - confirmVariant / cancelVariant: passed to ModalActionsFooter (ScfoButton variants).
 * - footerLeft: ReactNode for the footer's left slot (e.g. a destructive action); wins over footer/footerTwo.
 */
const CustomModal = ({
  open,
  onClose,
  title,
  content,
  onConfirm,
  confirmLabel,
  confirmDisabled,
  isLoading = false,
  size = 'tiny',
  cancelarLabel = 'Cancelar',
  classNameTitle,
  footer,
  footerTwo,
  footerLeft,
  dimmer = true,
  classNameContent,
  classNameModal,
  modalStyle,
  customActions,
  classNameActions,
  scrolling,
  hideActions = false,
  hideHeader = false,
  renderHeader,
  confirmVariant = 'primary',
  cancelVariant = 'ghost',
  closeOnDimmerClick = true,
  closeOnEscape = true,
  closeIcon = false,
  ...rest
}) => {
  const contentStyle = getCustomModalContentStyle(classNameContent);

  const showDefaultHeader = !hideHeader && renderHeader === undefined;
  const showCustomHeader = !hideHeader && renderHeader !== undefined;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size={size}
      dimmer={dimmer}
      scrolling={scrolling}
      closeOnDimmerClick={closeOnDimmerClick}
      closeOnEscape={closeOnEscape}
      closeIcon={closeIcon}
      className={`custom-modal ${classNameModal || ''}`.trim()}
      style={{
        borderRadius: '16px',
        ...modalStyle
      }}
      {...rest}
    >
      {showCustomHeader ? (
        renderHeader
      ) : showDefaultHeader ? (
        <Modal.Header
          className={classNameTitle}
          style={{ textAlign: 'center', fontSize: '14px' }}
        >
          {title}
        </Modal.Header>
      ) : null}
      <Modal.Content className={classNameContent} style={contentStyle}>
        {content}
      </Modal.Content>
      {!hideActions && (
        <Modal.Actions className={classNameActions}>
          {customActions ? (
            customActions
          ) : (
            <ModalActionsFooter
              left={
                footerLeft ? (
                  footerLeft
                ) : footer ? (
                  <p className="tw-text-base tw-text-[#3C4B64] tw-font-semibold">
                    {footer}: {formatMoneyWithCurrency(footerTwo)}
                  </p>
                ) : null
              }
              cancelLabel={cancelarLabel}
              onCancel={onClose}
              confirmLabel={confirmLabel}
              onConfirm={onConfirm}
              confirmDisabled={confirmDisabled}
              confirmLoading={isLoading}
              confirmVariant={confirmVariant}
              cancelVariant={cancelVariant}
            />
          )}
        </Modal.Actions>
      )}
    </Modal>
  );
};

export default CustomModal;

/** Footer estándar (ScfoButton) para `customActions` cuando no basta confirmLabel/onConfirm. */
export { ModalActionsFooter as CustomModalActions };
